import React, { useState, useEffect } from 'react';
import { 
  Brain, Plus, Edit, Eye, Check, X, TrendingUp, TrendingDown,
  Clock, AlertCircle, CheckCircle, Archive, RotateCcw
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { useToast } from '../../../hooks/use-toast';

const ModelsView = () => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFeature, setSelectedFeature] = useState('all');
  const { toast } = useToast();
  const API_BASE_URL = '/api';

  const features = [
    { id: 'all', label: 'All Features' },
    { id: 'affirmations', label: 'Affirmations' },
    { id: 'journaling', label: 'Journaling' },
    { id: 'companion', label: 'Conchi (AI Companion)' },
    { id: 'divine_guidance', label: 'Divine Guidance' }
  ];

  useEffect(() => {
    fetchModels();
  }, [selectedFeature]);

  const fetchModels = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const featureParam = selectedFeature !== 'all' ? `?feature_name=${selectedFeature}` : '';
      const response = await fetch(`${API_BASE_URL}/ai-controls/models${featureParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch models');

      const data = await response.json();
      setModels(data.data || []);
    } catch (error) {
      toast({
        title: '❌ Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      published: 'bg-green-100 text-green-800 border-green-200',
      draft: 'bg-gray-100 text-gray-800 border-gray-200',
      preview: 'bg-blue-100 text-blue-800 border-blue-200',
      archived: 'bg-amber-100 text-amber-800 border-amber-200'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${styles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatFeatureName = (name) => {
    return name.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
            AI Model Configurations
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage which AI model powers each feature with health metrics
          </p>
        </div>
      </div>

      {/* Feature Filter */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {features.map(feature => (
          <button
            key={feature.id}
            onClick={() => setSelectedFeature(feature.id)}
            className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
              selectedFeature === feature.id
                ? 'bg-gradient-to-r from-violet-500 to-purple-500 text-white shadow-lg'
                : 'bg-white text-gray-600 hover:text-gray-900 border border-gray-200'
            }`}
          >
            {feature.label}
          </button>
        ))}
      </div>

      {/* Models Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : models.length === 0 ? (
        <Card className="p-12 text-center">
          <Brain className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No model configurations found</p>
          <p className="text-sm text-gray-500 mt-2">Import your configs to get started</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {models.map(model => (
            <Card key={model.id} className="p-6 hover:shadow-lg transition-all border-2 border-gray-100">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Brain className="w-5 h-5 text-violet-600" />
                    <h3 className="font-semibold text-gray-900">
                      {formatFeatureName(model.feature_name)}
                    </h3>
                    {getStatusBadge(model.status)}
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">{model.provider}</span> · {model.model_name}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Version {model.version}
                  </div>
                </div>
              </div>

              {/* Health Metrics */}
              {model.health_metrics && (
                <div className="grid grid-cols-2 gap-4 mb-4 p-4 bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg">
                  <div>
                    <div className="text-xs text-gray-600 mb-1">24h Requests</div>
                    <div className="text-lg font-semibold text-gray-900">
                      {model.health_metrics.requests_24h || 0}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600 mb-1">Success Rate</div>
                    <div className="text-lg font-semibold text-green-600">
                      {model.health_metrics.success_rate || 0}%
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600 mb-1">Avg Latency</div>
                    <div className="text-lg font-semibold text-blue-600">
                      {model.health_metrics.avg_latency_ms || 0}ms
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600 mb-1">24h Cost</div>
                    <div className="text-lg font-semibold text-amber-600">
                      ${(model.health_metrics.total_cost_24h || 0).toFixed(4)}
                    </div>
                  </div>
                </div>
              )}

              {/* Parameters */}
              <div className="text-xs text-gray-600 space-y-1 mb-4">
                {model.temperature && (
                  <div>Temperature: <span className="font-medium">{model.temperature}</span></div>
                )}
                {model.max_tokens && (
                  <div>Max Tokens: <span className="font-medium">{model.max_tokens}</span></div>
                )}
                <div>Timeout: <span className="font-medium">{model.timeout}s</span></div>
              </div>

              {/* Actions */}
              {model.status !== 'published' && (
                <div className="flex gap-2">
                  <Button 
                    size="sm"
                    className="w-full text-xs bg-gradient-to-r from-violet-500 to-purple-500"
                  >
                    <Check className="w-3 h-3 mr-1" />
                    Publish
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ModelsView;